import {createSlice, PayloadAction, createAction} from '@reduxjs/toolkit';
import {FeedListItem, FeedDetailItem, ReturnItem} from '../../types/FeedItem';

export type NetFlowPoint = {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  indiv_net: number;
  foreign_net: number;
  inst_net: number;
};

interface FeedState {
  sector: string;
  sort: 'latest' | 'impact';

  feeds: FeedListItem[];
  selectedFeed: FeedDetailItem | null;

  topImpactFeeds: FeedListItem[];
  topReturnSimilarityFeeds: FeedListItem[];

  returnData: ReturnItem[];

  netSeries: NetFlowPoint[];
  netSeriesLoading: boolean;
  netSeriesError: string | null;
  netSeriesTicker: string | null;
  netSeriesFrom: string | null;
  netSeriesTo: string | null;

  loading: boolean;
  error: string | null;
}

const initialState: FeedState = {
  sector: 'chip',
  sort: 'latest',

  feeds: [],
  selectedFeed: null,

  topImpactFeeds: [],
  topReturnSimilarityFeeds: [],

  returnData: [],

  netSeries: [],
  netSeriesLoading: false,
  netSeriesError: null,
  netSeriesTicker: null,
  netSeriesFrom: null,
  netSeriesTo: null,

  loading: false,
  error: null,
};

export const fetchFeedsStart = createAction<{
  sector: string;
  sort: 'latest' | 'impact'
}>('feeds/fetchFeeds');
export const fetchFeedsSuccess = createAction<FeedListItem[]>('feeds/fetchFeedsSuccess');
export const fetchFeedsFailure = createAction<string | undefined>('feeds/fetchFeedsFailure');

export const fetchFeedDetailStart = createAction<{
  feedId: string;
  sector?: string
}>('feeds/fetchFeedDetail');
export const fetchFeedDetailSuccess = createAction<FeedDetailItem>('feeds/fetchFeedDetailSuccess');
export const fetchFeedDetailFailure = createAction<string | undefined>('feeds/fetchFeedDetailFailure');

export const fetchReturnDataSuccess = createAction<ReturnItem[]>('feeds/fetchReturnDataSuccess');
export const fetchReturnDataFailure = createAction('feeds/fetchReturnDataFailure');

export const saveEventRequest = createAction<{
  eventId: string;
  sector?: string
}>('feed/saveEvent/request');
export const removeSavedEventRequest = createAction<{
  eventId: string
}>('feed/removeSavedEvent/request');

export const createAlertFromEventRequest = createAction<{
  eventId: string;
  channels?: { webpush: boolean; email: boolean };
  filters?: any | null;
  sector?: string;
}>('feed/createAlertFromEvent/request');

export const runBacktestRequest = createAction<{
  eventId?: string | null;
  strategyKey: string;
  params?: Record<string, any>;
  dateFrom?: string | null;
  dateTo?: string | null;
}>('feed/runBacktest/request');

export const addWatchlistRequest = createAction<{
  tickers: { code: string; name: string; market?: string }[];
}>('feed/addWatchlist/request');

export const removeWatchlistRequest = createAction<{
  tickers: string[];
}>('feed/removeWatchlist/request');

export const fetchNetSeriesStart = createAction<{
  ticker: string;
  from: string;
  to: string;
}>('feeds/fetchNetSeries');

export const fetchNetSeriesSuccess = createAction<{
  ticker: string;
  from: string;
  to: string;
  data: NetFlowPoint[];
}>('feeds/fetchNetSeriesSuccess');

export const fetchNetSeriesFailure = createAction<{
  ticker: string;
  from: string;
  to: string;
  error: string;
}>('feeds/fetchNetSeriesFailure');

const feedSlice = createSlice({
  name: 'feeds',
  initialState,
  reducers: {
    setSector(state, action: PayloadAction<string>) {
      state.sector = action.payload;
    },
    setSort(state, action: PayloadAction<'latest' | 'impact'>) {
      state.sort = action.payload;
    },
    setSelectedFeed(state, action: PayloadAction<FeedDetailItem | null>) {
      state.selectedFeed = action.payload;
    },
    setFeeds(state, action: PayloadAction<FeedListItem[]>) {
      state.feeds = action.payload;
    },
    setTopImpactFeeds(state, action: PayloadAction<FeedListItem[]>) {
      state.topImpactFeeds = action.payload;
    },
    setTopReturnSimilarityFeeds(state, action: PayloadAction<FeedListItem[]>) {
      state.topReturnSimilarityFeeds = action.payload;
    },
    setFeedLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setFeedError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setNetSeriesParams(
        state,
        action: PayloadAction<{ ticker: string | null; from: string | null; to: string | null }>
    ) {
      state.netSeriesTicker = action.payload.ticker;
      state.netSeriesFrom = action.payload.from;
      state.netSeriesTo = action.payload.to;
    },
    clearNetSeries(state) {
      state.netSeries = [];
      state.netSeriesLoading = false;
      state.netSeriesError = null;
    },
  },
  extraReducers: (builder) => {
    builder
    .addCase(fetchFeedsStart, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(fetchFeedsSuccess, (state, action) => {
      state.feeds = action.payload;
      state.loading = false;
    })
    .addCase(fetchFeedsFailure, (state, action) => {
      state.loading = false;
      state.error = action.payload ?? '목록 조회 실패';
    })

    .addCase(fetchFeedDetailStart, (state) => {
      state.loading = true;
      state.error = null;
      state.selectedFeed = null;
    })
    .addCase(fetchFeedDetailSuccess, (state, action) => {
      state.loading = false;
      state.selectedFeed = action.payload;
    })
    .addCase(fetchFeedDetailFailure, (state, action) => {
      state.loading = false;
      state.error = action.payload ?? '상세 조회 실패';
      state.selectedFeed = null;
    })

    .addCase(fetchReturnDataSuccess, (state, action) => {
      state.returnData = action.payload;
    })
    .addCase(fetchReturnDataFailure, (state) => {
      state.returnData = [];
    })

    .addCase(fetchNetSeriesStart, (state, action) => {
      state.netSeriesLoading = true;
      state.netSeriesError = null;
      state.netSeries = [];
      state.netSeriesTicker = action.payload.ticker;
      state.netSeriesFrom = action.payload.from;
      state.netSeriesTo = action.payload.to;
    })
    .addCase(fetchNetSeriesSuccess, (state, action) => {
      state.netSeriesLoading = false;
      state.netSeriesError = null;
      state.netSeries = action.payload.data ?? [];
      state.netSeriesTicker = action.payload.ticker;
      state.netSeriesFrom = action.payload.from;
      state.netSeriesTo = action.payload.to;
    })
    .addCase(fetchNetSeriesFailure, (state, action) => {
      state.netSeriesLoading = false;
      state.netSeriesError = action.payload.error ?? '데이터 조회 실패';
      state.netSeries = [];
      state.netSeriesTicker = action.payload.ticker;
      state.netSeriesFrom = action.payload.from;
      state.netSeriesTo = action.payload.to;
    });
  },
});

export const {
  setSector,
  setSort,
  setSelectedFeed,
  setFeeds,
  setTopImpactFeeds,
  setTopReturnSimilarityFeeds,
  setFeedLoading,
  setFeedError,
  setNetSeriesParams,
  clearNetSeries,
} = feedSlice.actions;

export default feedSlice.reducer;